// This HEADER is different and used only on the Homepage
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import Container from "@mui/material/Container"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import { styled } from "@mui/material/styles"
import { Link } from "gatsby"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import shortid from "shortid"

import LangContext from "../../contexts/LangContext"
import { useSearch } from "../../contexts/GlobalSearchProvider"
import SubMenuItem from "./SubMenuItem"

const PREFIX = "MobileMenu"

const classes = {
  wrapper: `${PREFIX}-wrapper`,
  closeButton: `${PREFIX}-closeButton`,
  searchButton: `${PREFIX}-searchButton`,
  list: `${PREFIX}-list`,
  item: `${PREFIX}-item`,
  link: `${PREFIX}-link`,
  inputWrapper: `${PREFIX}-inputWrapper`,
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.wrapper}`]: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: "100%",
    zIndex: 100000,
    background: theme.palette.aresBlue.main,
    color: "#ffffff",
    pointerEvents: "auto",
  },

  [`& .${classes.closeButton}`]: {
    color: theme.palette.common.white,
  },

  [`& .${classes.searchButton}`]: {
    color: theme.palette.common.white,
  },

  [`& .${classes.list}`]: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    maxHeight: "calc(100vh - 220px)",
    "overflow-y": "scroll",
  },

  [`& .${classes.item}`]: {
    "&:first-of-type": {
      borderTop: "1px solid #013754",
    },
    borderBottom: "1px solid #013754",
  },

  [`& .${classes.link}`]: {
    display: "block",
    pointerEvents: "auto",
    color: "#fff",
    lineHeight: "1.2em",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    fontSize: 22,
    fontWeight: 400,
    "&:hover": {
      textDecoration: "underline",
    },
  },

  [`& .${classes.inputWrapper}`]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    cursor: "pointer",

    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.common.white,
      cursor: "pointer",
    },
    "& .MuiInputLabel-shrink": {
      backgroundColor: theme.palette.common.white,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.common.white,
    },
  },
}))

const MobileMenu = ({ menu, location, toggleMobileMenu }) => {
  const lang = useContext(LangContext)
  const { openSearch } = useSearch()

  const handleSearchClick = () => {
    // Close mobile menu first, then open enhanced search
    toggleMobileMenu()
    // Small delay to ensure mobile menu closes before opening search dialog
    setTimeout(() => {
      openSearch()
    }, 100)
  }

  const i18n = lang.i18n[lang.locale]

  const renderMenuItem = item => {
    if (item.sublinks) {
      return (
        <li key={shortid.generate()} className={classes.item}>
          <SubMenuItem item={item} toggleMobileMenu={toggleMobileMenu} />
        </li>
      )
    }

    return (
      <li key={shortid.generate()} className={classes.item}>
        <Link
          to={
            i18n.default
              ? `/${item.internal}`
              : `/${i18n.path}/${item.internal}/`
          }
          className={classes.link}
          onClick={toggleMobileMenu}
        >
          {item.label}
        </Link>
      </li>
    )
  }

  return (
    <StyledGrid
      className={classes.wrapper}
      container
      direction="column"
      justifyContent="space-between"
      alignItems="stretch"
      style={{ height: "100%" }}
    >
      <Grid item>
        <Container>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={1}
          >
            <Grid item xs={10}>
              <div className={classes.inputWrapper} onClick={handleSearchClick}>
                <TextField
                  variant="outlined"
                  type="text"
                  label="Găsește informații medicale și de sănătate"
                  fullWidth
                  placeholder="Apasă pentru căutare avansată..."
                  onClick={handleSearchClick}
                  InputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          aria-label="deschide căutarea avansată"
                          onClick={handleSearchClick}
                          size="large"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={toggleMobileMenu}
                className={classes.closeButton}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Container>
        <nav>
          <ul className={classes.list}>
            {menu.map(item => renderMenuItem(item))}
          </ul>
        </nav>
      </Grid>
    </StyledGrid>
  )
}

export default MobileMenu

MobileMenu.propTypes = {
  menu: PropTypes.oneOfType([PropTypes.array]).isRequired,
  location: PropTypes.object.isRequired,
  toggleMobileMenu: PropTypes.func.isRequired,
}